import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import tenantConfigFallback from "./tenantConfigFallback.json";

const TenantConfigContext = createContext(null);

const API_DOMAIN = process.env.REACT_APP_ADMIN_PORTAL_API_DOMAIN || "";
const TENANT_CONFIG_ENDPOINT =
  process.env.REACT_APP_TENANT_CONFIG_ENDPOINT || "/api/Login/tenant-config";
const TENANT_CONFIG_CACHE_KEY = "tenant-config:api";

const localFallbackTenantConfig = {
  ...tenantConfigFallback,
  homeParagraphs: Array.isArray(tenantConfigFallback.homeParagraphs)
    ? tenantConfigFallback.homeParagraphs
    : [],
};

const normalizeApiPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {};
  }

  if (payload.config && typeof payload.config === "object") {
    return payload.config;
  }

  if (payload.data && typeof payload.data === "object") {
    return payload.data;
  }

  return payload;
};

const toAbsoluteEndpoint = (endpoint) => {
  if (!endpoint) return "";
  if (/^https?:\/\//i.test(endpoint)) return endpoint;

  const apiDomain = API_DOMAIN?.replace(/\/$/, "");
  const routePath = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  return `${apiDomain}${routePath}`;
};

const getCurrentDomain = () =>
  process.env.REACT_APP_TENANT_CONFIG_DOMAIN || window.location.hostname;

const getCachedTenantConfig = (domain) => {
  try {
    const rawValue = localStorage.getItem(TENANT_CONFIG_CACHE_KEY);
    if (!rawValue) return null;

    const cachedValue = JSON.parse(rawValue);
    if (cachedValue?.domain !== domain || !cachedValue?.config) return null;

    return cachedValue.config;
  } catch {
    localStorage.removeItem(TENANT_CONFIG_CACHE_KEY);
    return null;
  }
};

const setCachedTenantConfig = (domain, config) => {
  try {
    localStorage.setItem(
      TENANT_CONFIG_CACHE_KEY,
      JSON.stringify({ domain, config, cachedAt: new Date().toISOString() })
    );
  } catch {
    // Cache failures should not block tenant config rendering.
  }
};

const getApiErrorMessage = async (response) => {
  try {
    const payload = await response.json();
    return payload?.message || payload?.detail || `Tenant config API returned ${response.status}`;
  } catch {
    return `Tenant config API returned ${response.status}`;
  }
};

const fetchTenantConfig = async () => {
  const endpoint = toAbsoluteEndpoint(TENANT_CONFIG_ENDPOINT);
  if (!endpoint) {
    throw new Error("Tenant config endpoint is not configured.");
  }

  const domain = getCurrentDomain();
  const query = new URLSearchParams({ domain }).toString();

  const response = await fetch(`${endpoint}?${query}`, {
    method: "GET",
    headers: {
      "x-api-key": process.env.REACT_APP_WEB_API_KEY,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response));
  }

  const payload = await response.json();
  const apiConfig = normalizeApiPayload(payload);

  const config = {
    ...localFallbackTenantConfig,
    ...apiConfig,
    homeParagraphs: Array.isArray(apiConfig.homeParagraphs)
      ? apiConfig.homeParagraphs
      : localFallbackTenantConfig.homeParagraphs,
  };

  setCachedTenantConfig(domain, config);
  return config;
};

export const TenantConfigProvider = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState(() => {
    const domain = getCurrentDomain();
    return getCachedTenantConfig(domain) || localFallbackTenantConfig;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canLogin, setCanLogin] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      try {
        const config = await fetchTenantConfig();
        if (isMounted) {
          setTenantConfig(config);
          setError("");
          setCanLogin(true);
        }
      } catch (err) {
        if (isMounted) {
          setTenantConfig(localFallbackTenantConfig);
          setError(err?.message || "Failed to load tenant config.");
          setCanLogin(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({ tenantConfig, loading, error, canLogin }),
    [tenantConfig, loading, error, canLogin]
  );

  return <TenantConfigContext.Provider value={value}>{children}</TenantConfigContext.Provider>;
};

export const useTenantConfig = () => {
  const context = useContext(TenantConfigContext);
  if (!context) {
    throw new Error("useTenantConfig must be used inside TenantConfigProvider");
  }
  return context;
};

export default TenantConfigContext;
